import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { LoadingButton } from "@mui/lab";
import { ExpressPaymentDialog } from "src/sections/dashboard/stripe/express-payment-dialog";
import useUserDataProvider from "src/contexts/userData-context";
import useHousehold from "src/contexts/household-context";
import { Household } from "src/types/snugtotal";
import { useRouter } from "src/hooks/use-router";

interface PersonalPlanSelectorProps {
  // State
  maritalStatus: string;
  recommendedPlan: string | null;
  recommendedPlanExplainer: string | null;
  willType: "single_will" | "two_wills";
  trustType: "joint" | "two_trusts" | "single_trust";
  saving: boolean;
  checkedGoals: {
    easySetup: boolean;
    guardianship: boolean;
    incapacity: boolean;
    minimizeTaxes: boolean;
  };
  checkedSituations: {
    nonUs: boolean;
    spouseNonUs: boolean;
    charity: boolean;
    divorce: boolean;
    international: boolean;
    creditors: boolean;
    fight: boolean;
    none: boolean;
  };

  // Membership
  // includeMembership: boolean;
  // setIncludeMembership: (value: boolean) => void;

  // Handlers
  setWillType: (type: "single_will" | "two_wills") => void;
  setTrustType: (type: "joint" | "two_trusts" | "single_trust") => void;
  onBack?: () => void;
  onSavePlan: (
    foundationValue: 'single_will' | 'two_wills' | 'single_trust' | 'two_trusts' | 'joint_trust',
    // includeMembership: boolean
  ) => Promise<void>;

  // Utility
  formatUSD: (amount: number) => string;
  optionCost: (value: string) => number;
}

export const PersonalPlanSelector = ({
  maritalStatus,
  recommendedPlan,
  recommendedPlanExplainer,
  willType,
  trustType,
  saving,
  checkedGoals,
  checkedSituations,
  // includeMembership,
  // setIncludeMembership,
  setWillType,
  setTrustType,
  onBack,
  onSavePlan,
  formatUSD,
  optionCost,
}: PersonalPlanSelectorProps) => {
  // Track platform trial dialog stat
  const router = useRouter();
  // Payment dialog states
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const { userData } = useUserDataProvider();
  const {
    selectedHousehold,
    paymentRequiredData,
    paymentRequiredLoading,
    paymentsSaving,
    currentPaymentIntent,
    handleCreateOrUpdatePaymentIntent,
    handleClearPaymentIntent,
    handleGetPaymentRequired,
    createSetupIntent,
    createTrialSubscription,
  } = useHousehold();

  // Derive household-related props from selectedHousehold
  const allowJointTrust =
    selectedHousehold?.household_role?.allow_joint_trust ?? true;
  const allowIndividualTrust =
    selectedHousehold?.household_role?.allow_individual_trust ?? true;
  // const allowMembership =
  //   selectedHousehold?.household_role?.allow_membership ?? false;
  const willPrice =
    selectedHousehold?.household_role?.professional_pricing_option === "CUSTOM"
      ? (selectedHousehold?.household_role?.will_price ?? 0) / 100
      : (selectedHousehold?.professional_group?.default_will_price ?? 0) / 100;
  const trustPrice =
    selectedHousehold?.household_role?.professional_pricing_option === "CUSTOM"
      ? (selectedHousehold?.household_role?.trust_price ?? 0) / 100
      : (selectedHousehold?.professional_group?.default_trust_price ?? 0) / 100;
  const showPricing =
    selectedHousehold?.household_role?.paywall_option !==
      "direct" &&
    selectedHousehold?.professional_group?.allow_client_charges === true;
  // The amount each card actually shows (mirrors the formatUSD arg below). When
  // it's $0 (no charge) we hide the price entirely, same as the direct-charge
  // case -- there's nothing to collect, so "$0" reads as noise.
  const willDisplayAmount =
    willType === "two_wills" && maritalStatus !== "single" ? willPrice * 2 : willPrice;
  const trustDisplayAmount =
    trustType === "joint"
      ? optionCost("joint")
      : trustType === "single_trust"
      ? trustPrice
      : optionCost("trust");
  const canWill =
    (selectedHousehold?.professional_group?.allow_wills ?? true) &&
    !(selectedHousehold?.household_role?.block_will ?? false);
  const canTrust =
    (selectedHousehold?.professional_group?.allow_trusts ?? true) &&
    !(selectedHousehold?.household_role?.block_trust ?? false);
  // Apply direct_charge_document_selection constraint if professional has pre-selected a plan
  // const directChargeSelection =
  //   selectedHousehold?.household_role?.direct_charge_document_selection;
  // const constrainedCanWill =
  //   canWill && (!directChargeSelection || directChargeSelection === "will");
  // const constrainedCanTrust =
  //   canTrust &&
  //   (!directChargeSelection ||
  //     directChargeSelection === "trust" ||
  //     directChargeSelection === "joint");
  const allow_quitclaim_deed =
    selectedHousehold?.professional_group?.allow_quitclaim_deed ?? false;
  const enforceAnnual =
    selectedHousehold?.professional_group?.enforce_annual_payment_check ?? false;

  // Check if any situations are checked (excluding "none")
  const hasCheckedSituations = Object.entries(checkedSituations)
    .filter(([key]) => key !== "none")
    .some(([, val]) => val);

  // Auto-set willType and trustType for single users since they can't use the dropdown
  useEffect(() => {
    if (maritalStatus === "single") {
      setWillType("single_will");
      setTrustType("single_trust");
    }
  }, [maritalStatus, setWillType, setTrustType]);

  const checkForPaymentDialogRequirement = async (household: Household | null) => {
    // if the client is a direct charge client, membership is allowed and on, and there is no payment method, surface payment method dialog
    // if the client is a paywall at signup client, membership is allowed and on, and payment is required, surface payment dialog.
    if (!household) {
      return false;
    }

    // ON_PRO_TAB clients should NEVER see a paywall - they are billed directly to the professional
    if (household.household_role?.paywall_option === "direct") {
      return true;
    }

    if (household.household_role?.paywall_option === "signup") {
      // Paywall at signup - check if payment is required, open ExpressPaymentDialog
      const product =
        (household.head_of_household?.estate_plan_foundation === "single_will" || household.head_of_household?.estate_plan_foundation === "two_wills") 
          ? "WILL"
          : "TRUST";

      if (paymentRequiredData) {
        const basePrice =
          product === "WILL" ? paymentRequiredData.will_price : paymentRequiredData.trust_price;
        const amountDue = Math.max(
          0,
          basePrice -
            (paymentRequiredData.total_amount_paid || 0) -
            (paymentRequiredData.credit_available || 0)
        );

        if (amountDue > 0) {
          setPaymentDialogOpen(true);
          return false;
        }
      }
    }
    return true;
  };

  return (
    <Stack direction="column" spacing={2}>
      <Stack spacing={1} sx={{ textAlign: "center" }}>
        <Typography variant="h4">Choose your plan</Typography>
        <Typography variant="body2" color="text.secondary">
          Wills and Trusts are the foundation of any estate plan. You can update
          your decision later.
        </Typography>
      </Stack>

      {hasCheckedSituations && (
        <Box
          sx={{
            backgroundColor: "#FFF0F0",
            border: "1px solid",
            borderColor: "error.main",
            borderRadius: 1,
            p: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" color="error.main">
            Your situation may involve complexities that often benefit from working with an attorney.
          </Typography>
        </Box>
      )}

      <Grid container spacing={3} sx={{ justifyContent: "center" }}>
        {/* Will Plan Column */}
        {canWill && (
          <Grid
            xs={12}
            md={6}
            sx={{
              order: {
                xs: recommendedPlan === "will" ? 0 : 1,
                md: 0,
              },
            }}
          >
            <Card
              variant={
                recommendedPlan === "will"
                  ? "elevation"
                  : "outlined"
              }
              elevation={
                recommendedPlan === "will" ? 5 : 0
              }
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                p: 3,
                border:
                  recommendedPlan === "will"
                    ? "2px solid #000"
                    : undefined,
                borderRadius: 1,
                position: "relative",
                overflow: "visible",
              }}
            >
              {recommendedPlan === "will" && (
                <Tooltip title={recommendedPlanExplainer} arrow placement="top">
                  <Chip
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        Best Fit
                        <InfoOutlinedIcon sx={{ fontSize: "0.875rem" }} />
                      </Box>
                    }
                    size="small"
                    sx={{
                      position: "absolute",
                      top: -12,
                      left: "50%",
                      transform: "translateX(-50%)",
                      height: 24,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      zIndex: 10,
                      cursor: "pointer",
                      backgroundColor: "#000",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#333",
                      },
                    }}
                  />
                </Tooltip>
              )}
              <Box sx={{ minHeight: maritalStatus !== "single" ? 240 : 0 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <Typography variant="h4">
                    Will Plan
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Essential protection for straightforward estates.
                </Typography>

                {showPricing && willDisplayAmount > 0 && (
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {formatUSD(willDisplayAmount)}
                  </Typography>
                )}

                <Typography
                  variant="caption"
                  color="warning.main"
                  sx={{ mb: 2, display: "block" }}
                >
                  Probate required (9–15 months)
                </Typography>

                {/* Will Type Selection */}
                {maritalStatus !== "single" && (
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <Select
                      value={willType}
                      onChange={(e) =>
                        setWillType(e.target.value as "single_will" | "two_wills")
                      }
                      onClick={(e) => e.stopPropagation()}
                      // disabled={!!directChargeSelection}
                    >
                      <MenuItem value="two_wills">
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            Individual Wills (2x Wills)
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Complete estate plan for couples
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="single_will">
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            Just Me
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Individual estate plan for one person
                          </Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Box>

              <LoadingButton
                onClick={async () => {
                  const foundationValue = willType;
                  // const paymentOption = "HOUSEHOLD"; // Always charge for whole household at signup
                  await onSavePlan(
                    foundationValue,
                    // includeMembership
                  );
                  const allowProgress = await checkForPaymentDialogRequirement(selectedHousehold)
                  if (allowProgress) {
                    router.push("/express");
                  }
                }}
                disabled={saving}
                fullWidth
                color={
                  recommendedPlan === "will" || !canTrust
                    ? "primary"
                    : "inherit"
                }
                sx={{ mb: 2, borderRadius: 1 }}
                type="button"
                variant="contained"
                loading={saving}
              >
                Continue with Will Plan
              </LoadingButton>

              {/* {allowMembership && (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={includeMembership}
                      onChange={(e) => setIncludeMembership(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ ml: 0.5 }}>
                      Include free trial of{" "}
                      <Box
                        component="span"
                        onClick={(e) => {
                          e.preventDefault();
                          setPlatformTrialOpen(true);
                        }}
                        sx={{
                          textDecoration: "underline",
                          cursor: "pointer",
                          "&:hover": {
                            color: "primary.main",
                          },
                        }}
                      >
                        Premium features
                      </Box>
                    </Typography>
                  }
                  sx={{ mb: 3, ml: 0 }}
                />
              )} */}

              <Divider sx={{ mb: 3 }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {/* {includeMembership ? (
                  <>
                    <Typography variant="subtitle2">
                      Premium Features:
                    </Typography>

                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      <CheckOutlinedIcon
                        sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                      />
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2">
                          {enforceAnnual
                            ? "Will and Trust updates for 1 year"
                            : "Unlimited Will and Trust updates"}
                        </Typography>
                        <Tooltip
                          title={
                            enforceAnnual
                              ? "Update your Will or Trust documents for 1 year to keep your plans up to date."
                              : "Unlimited lifetime updates to your Will or Trust documents makes it simple to keep your plans up to date."
                          }
                          arrow
                        >
                          <WorkspacePremiumIcon
                            sx={{
                              fontSize: 16,
                              color: "text.secondary",
                              cursor: "pointer",
                            }}
                            onClick={() => setPlatformTrialOpen(true)}
                          />
                        </Tooltip>
                      </Stack>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      <CheckOutlinedIcon
                        sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                      />
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2">
                          Secure Digital Vault
                        </Typography>
                        <Tooltip
                          title="Store important documents, digital assets, and financial information in a secure digital vault."
                          arrow
                        >
                          <WorkspacePremiumIcon
                            sx={{
                              fontSize: 16,
                              color: "text.secondary",
                              cursor: "pointer",
                            }}
                            onClick={() => setPlatformTrialOpen(true)}
                          />
                        </Tooltip>
                      </Stack>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      <CheckOutlinedIcon
                        sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                      />
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2">
                          Emergency Access Cards
                        </Typography>
                        <Tooltip
                          title="Physical wallet cards that provide your trusted contacts with emergency access to critical information."
                          arrow
                        >
                          <WorkspacePremiumIcon
                            sx={{
                              fontSize: 16,
                              color: "text.secondary",
                              cursor: "pointer",
                            }}
                            onClick={() => setPlatformTrialOpen(true)}
                          />
                        </Tooltip>
                      </Stack>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      <CheckOutlinedIcon
                        sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                      />
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2">Family Sharing</Typography>
                        <Tooltip
                          title="Securely share your account and documents with family members and beneficiaries."
                          arrow
                        >
                          <WorkspacePremiumIcon
                            sx={{
                              fontSize: 16,
                              color: "text.secondary",
                              cursor: "pointer",
                            }}
                            onClick={() => setPlatformTrialOpen(true)}
                          />
                        </Tooltip>
                      </Stack>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      <CheckOutlinedIcon
                        sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                      />
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2">
                          Video Legacy Messages
                        </Typography>
                        <Tooltip
                          title="Record and store personal video messages for your loved ones."
                          arrow
                        >
                          <WorkspacePremiumIcon
                            sx={{
                              fontSize: 16,
                              color: "text.secondary",
                              cursor: "pointer",
                            }}
                            onClick={() => setPlatformTrialOpen(true)}
                          />
                        </Tooltip>
                      </Stack>
                    </Box>
                  </>
                ) : ( */}
                  <>
                    <Typography variant="subtitle2">Key Features:</Typography>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      <CheckOutlinedIcon
                        sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                      />
                      <Typography variant="body2">
                        {enforceAnnual ? "Document updates for 1 year" : "Unlimited document updates"}
                      </Typography>
                    </Box>
                    {checkedGoals.easySetup && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <CheckOutlinedIcon
                          sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                        />
                        <Typography variant="body2">
                          Quick and easy setup
                        </Typography>
                      </Box>
                    )}
                    {checkedGoals.guardianship && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <CheckOutlinedIcon
                          sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                        />
                        <Typography variant="body2">
                          Name guardians for minor children
                        </Typography>
                      </Box>
                    )}
                    {checkedGoals.incapacity && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <CheckOutlinedIcon
                          sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                        />
                        <Typography variant="body2">
                          Choose who makes decisions if you can't
                        </Typography>
                      </Box>
                    )}
                    {!checkedGoals.easySetup &&
                      !checkedGoals.guardianship &&
                      !checkedGoals.incapacity && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                          }}
                        >
                          <CheckOutlinedIcon
                            sx={{
                              color: "success.main",
                              fontSize: 20,
                              mt: 0.25,
                            }}
                          />
                          <Typography variant="body2">
                            Straightforward and affordable option
                          </Typography>
                        </Box>
                      )}
                  </>
                {/* )} */}
                <Typography variant="subtitle2">Documents Included:</Typography>

                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <CheckOutlinedIcon
                    sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                  />
                  <Typography variant="body2">
                    Last Will & Testament
                    {maritalStatus !== "single" && willType === "two_wills"
                      ? " (2x)"
                      : ""}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <CheckOutlinedIcon
                    sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                  />
                  <Typography variant="body2">
                    Health Care Directive (includes Living Will)
                    {maritalStatus !== "single" && willType === "two_wills"
                      ? " (2x)"
                      : ""}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <CheckOutlinedIcon
                    sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                  />
                  <Typography variant="body2">
                    Financial Power of Attorney
                    {maritalStatus !== "single" && willType === "two_wills"
                      ? " (2x)"
                      : ""}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <CheckOutlinedIcon
                    sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                  />
                  <Typography variant="body2">
                    {enforceAnnual ? "1 year of unlimited updates" : "Unlimited updates"}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
        )}

        {/* Trust Plan Column */}
        {canTrust && (
          <Grid
            xs={12}
            md={6}
            sx={{
              order: {
                xs:
                  recommendedPlan === "trust" || recommendedPlan === "joint_trust"
                    ? 0
                    : 1,
                md: 1,
              },
            }}
          >
            <Card
              variant={
                (recommendedPlan === "trust" || recommendedPlan === "joint_trust")
                  ? "elevation"
                  : "outlined"
              }
              elevation={
                (recommendedPlan === "trust" || recommendedPlan === "joint_trust")
                  ? 5
                  : 0
              }
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                p: 3,
                border:
                  (recommendedPlan === "trust" ||
                    recommendedPlan === "joint_trust")
                    ? "2px solid #000"
                    : undefined,
                borderRadius: 1,
                position: "relative",
                overflow: "visible",
              }}
            >
              {(recommendedPlan === "trust" || recommendedPlan === "joint_trust") && (
                  <Tooltip
                    title={recommendedPlanExplainer}
                    arrow
                    placement="top"
                  >
                    <Chip
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          Best Fit
                          <InfoOutlinedIcon sx={{ fontSize: "0.875rem" }} />
                        </Box>
                      }
                      size="small"
                      sx={{
                        position: "absolute",
                        top: -12,
                        left: "50%",
                        transform: "translateX(-50%)",
                        height: 24,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        zIndex: 10,
                        cursor: "pointer",
                        backgroundColor: "#000",
                        color: "#fff",
                        "&:hover": {
                          backgroundColor: "#333",
                        },
                      }}
                    />
                  </Tooltip>
                )}
              <Box sx={{ minHeight: maritalStatus !== "single" ? 240 : 0 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <Typography variant="h4">
                    Trust Plan
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Maximum protection and privacy for your family.
                </Typography>

                {showPricing && trustDisplayAmount > 0 && (
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {formatUSD(trustDisplayAmount)}
                  </Typography>
                )}

                <Typography
                  variant="caption"
                  color="success.main"
                  sx={{ mb: 2, display: "block" }}
                >
                  Faster asset distribution (1–3 months)
                </Typography>

                {/* Trust Type Selection */}
                {maritalStatus !== "single" && (
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <Select
                      value={trustType}
                      onChange={(e) =>
                        setTrustType(
                          e.target.value as "joint" | "two_trusts" | "single_trust"
                        )
                      }
                      onClick={(e) => e.stopPropagation()}
                      // disabled={!!directChargeSelection}
                    >
                      {allowJointTrust && (
                        <MenuItem value="joint">
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              Joint Living Trust{recommendedPlan === "joint_trust" ? " (Best Fit)" : ""}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Best for managing shared assets together
                            </Typography>
                          </Box>
                        </MenuItem>
                      )}
                      {allowIndividualTrust && (
                        <MenuItem value="two_trusts">
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              2x Individual Living Trusts{recommendedPlan === "trust" ? " (Best Fit)" : ""}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Best for separate property and blended families
                            </Typography>
                          </Box>
                        </MenuItem>
                      )}
                      {allowIndividualTrust && (
                        <MenuItem value="single_trust">
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              Just Me
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Individual estate plan for one person
                            </Typography>
                          </Box>
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                )}
              </Box>

              <LoadingButton
                onClick={async () => {
                  // Map UI values to backend values
                  // "joint" -> "joint", "two_individual" -> "trust", "one_individual" -> "trust"
                  const foundationValue =
                    trustType === "joint" ? "joint_trust" : trustType === "two_trusts" ? "two_trusts" : "single_trust";
                  await onSavePlan(
                    foundationValue,
                    // includeMembership
                  );
                 const allowProgress = await checkForPaymentDialogRequirement(selectedHousehold)
                  if (allowProgress) {
                    router.push("/express");
                  }
                }}
                disabled={saving}
                fullWidth
                color={
                  recommendedPlan === "trust" ||
                  recommendedPlan === "joint_trust" ||
                  !canWill
                    ? "primary"
                    : "inherit"
                }
                sx={{ mb: 2, borderRadius: 1 }}
                type="button"
                variant="contained"
                loading={saving}
              >
                Continue with Trust Plan
              </LoadingButton>

              {/* {allowMembership && (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={includeMembership}
                      onChange={(e) => setIncludeMembership(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ ml: 0.5 }}>
                      Include free trial of{" "}
                      <Box
                        component="span"
                        onClick={(e) => {
                          e.preventDefault();
                          setPlatformTrialOpen(true);
                        }}
                        sx={{
                          textDecoration: "underline",
                          cursor: "pointer",
                          "&:hover": {
                            color: "primary.main",
                          },
                        }}
                      >
                        Premium features
                      </Box>
                    </Typography>
                  }
                  sx={{ mb: 3, ml: 0 }}
                />
              )} */}

              <Divider sx={{ mb: 3 }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {/* {includeMembership ? (
                  <>
                    <Typography variant="subtitle2">
                      Premium Features:
                    </Typography>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      <CheckOutlinedIcon
                        sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                      />
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2">
                          {enforceAnnual
                            ? "Will and Trust updates for 1 year"
                            : "Unlimited Will and Trust updates"}
                        </Typography>
                        <Tooltip
                          title={
                            enforceAnnual
                              ? "Update your Will or Trust documents for 1 year to keep your plans up to date."
                              : "Unlimited lifetime updates to your Will or Trust documents makes it simple to keep your plans up to date."
                          }
                          arrow
                        >
                          <WorkspacePremiumIcon
                            sx={{
                              fontSize: 16,
                              color: "text.secondary",
                              cursor: "pointer",
                            }}
                            onClick={() => setPlatformTrialOpen(true)}
                          />
                        </Tooltip>
                      </Stack>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      <CheckOutlinedIcon
                        sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                      />
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2">
                          Secure Digital Vault
                        </Typography>
                        <Tooltip
                          title="Store important documents, digital assets, and financial information in a secure digital vault."
                          arrow
                        >
                          <WorkspacePremiumIcon
                            sx={{
                              fontSize: 16,
                              color: "text.secondary",
                              cursor: "pointer",
                            }}
                            onClick={() => setPlatformTrialOpen(true)}
                          />
                        </Tooltip>
                      </Stack>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      <CheckOutlinedIcon
                        sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                      />
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2">
                          Emergency Access Cards
                        </Typography>
                        <Tooltip
                          title="Physical wallet cards that provide your trusted contacts with emergency access to critical information."
                          arrow
                        >
                          <WorkspacePremiumIcon
                            sx={{
                              fontSize: 16,
                              color: "text.secondary",
                              cursor: "pointer",
                            }}
                            onClick={() => setPlatformTrialOpen(true)}
                          />
                        </Tooltip>
                      </Stack>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      <CheckOutlinedIcon
                        sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                      />
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2">Family Sharing</Typography>
                        <Tooltip
                          title="Securely share your account and documents with family members and beneficiaries."
                          arrow
                        >
                          <WorkspacePremiumIcon
                            sx={{
                              fontSize: 16,
                              color: "text.secondary",
                              cursor: "pointer",
                            }}
                            onClick={() => setPlatformTrialOpen(true)}
                          />
                        </Tooltip>
                      </Stack>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      <CheckOutlinedIcon
                        sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                      />
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2">
                          Video Legacy Messages
                        </Typography>
                        <Tooltip
                          title="Record and store personal video messages for your loved ones."
                          arrow
                        >
                          <WorkspacePremiumIcon
                            sx={{
                              fontSize: 16,
                              color: "text.secondary",
                              cursor: "pointer",
                            }}
                            onClick={() => setPlatformTrialOpen(true)}
                          />
                        </Tooltip>
                      </Stack>
                    </Box>
                  </>
                ) : ( */}
                  <>
                    <Typography variant="subtitle2">Key Features:</Typography>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      <CheckOutlinedIcon
                        sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                      />
                      <Typography variant="body2">
                        {enforceAnnual ? "Document updates for 1 year" : "Unlimited document updates"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      <CheckOutlinedIcon
                        sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                      />
                      <Typography variant="body2">
                        Avoid probate entirely
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      <CheckOutlinedIcon
                        sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                      />
                      <Typography variant="body2">
                        Maximum privacy for your family
                      </Typography>
                    </Box>
                    {checkedGoals.guardianship && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <CheckOutlinedIcon
                          sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                        />
                        <Typography variant="body2">
                          Name guardians for minor children
                        </Typography>
                      </Box>
                    )}
                    {checkedGoals.incapacity && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <CheckOutlinedIcon
                          sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                        />
                        <Typography variant="body2">
                          Choose who makes decisions if you can't
                        </Typography>
                      </Box>
                    )}
                    {checkedGoals.minimizeTaxes && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <CheckOutlinedIcon
                          sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                        />
                        <Typography variant="body2">
                          Minimize estate and inheritance taxes
                        </Typography>
                      </Box>
                    )}
                  </>
                {/* )
                } */}

                <Typography variant="subtitle2">Documents Included:</Typography>

                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <CheckOutlinedIcon
                    sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                  />
                  <Typography variant="body2">
                    {trustType === "joint"
                      ? "Joint Revocable Living Trust"
                      : maritalStatus === "single" || trustType === "single_trust"
                      ? "Individual Revocable Living Trust"
                      : "Individual Revocable Living Trust (2x)"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <CheckOutlinedIcon
                    sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                  />
                  <Typography variant="body2">
                    Pour Over Will
                    {maritalStatus !== "single" && trustType !== "single_trust"
                      ? "s (2x)"
                      : ""}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <CheckOutlinedIcon
                    sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                  />
                  <Typography variant="body2">
                    Certificate of Trust
                    {maritalStatus !== "single" && trustType === "two_trusts"
                      ? " (2x)"
                      : ""}
                  </Typography>
                </Box>
                {allow_quitclaim_deed && (
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <CheckOutlinedIcon
                      sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                    />
                    <Typography variant="body2">Quitclaim Deed</Typography>
                  </Box>
                )}
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <CheckOutlinedIcon
                    sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                  />
                  <Typography variant="body2">
                    Health Care Directive (includes Living Will)
                    {maritalStatus !== "single" && trustType !== "single_trust"
                      ? " (2x)"
                      : ""}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <CheckOutlinedIcon
                    sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                  />
                  <Typography variant="body2">
                    Financial Power of Attorney
                    {maritalStatus !== "single" && trustType !== "single_trust"
                      ? " (2x)"
                      : ""}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <CheckOutlinedIcon
                    sx={{ color: "success.main", fontSize: 20, mt: 0.25 }}
                  />
                  <Typography variant="body2">
                    {enforceAnnual ? "1 year of unlimited updates" : "Unlimited updates"}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
        )}

      </Grid>

      {onBack && (
        <Stack spacing={1} sx={{ textAlign: "center" }}>
          <Box>
            <Button variant="text" color="inherit" onClick={onBack}>
              Back
            </Button>
          </Box>
        </Stack>
      )}

      {/* Platform Trial Dialog */}
      {/* <PlatformTrialDialog
        open={platformTrialOpen}
        onClose={() => setPlatformTrialOpen(false)}
        createSetupIntent={createSetupIntent}
        createTrialSubscription={createTrialSubscription}
      /> */}

      {/* Payment Method Dialog for direct charge customers without payment method */}
      {/* <AddPaymentMethodDialog
        open={paymentMethodDialogOpen}
        onClose={() => setPaymentMethodDialogOpen(false)}
        onSuccess={async () => {
          setPaymentMethodDialogOpen(false);
          // Refresh payment methods
          if (userData?.ud_id && selectedHousehold) {
            // Payment method added successfully, continue with flow
            toast.success("Payment method added successfully!");
          }
        }}
        onSuccessRedirect="/express"
        ud_id={userData?.ud_id || null}
        createSetupIntent={createSetupIntent}
      /> */}

      {/* Payment Dialog for signup paywall customers */}
      {paymentDialogOpen && (
        <ExpressPaymentDialog
          open={paymentDialogOpen}
          onClose={() => {
            setPaymentDialogOpen(false);
            // User can now see plan selector again and uncheck membership if direct charge
          }}
          onSuccessRedirect="/express"
          product={selectedHousehold?.head_of_household?.estate_plan_foundation === 'joint_trust' || selectedHousehold?.head_of_household?.estate_plan_foundation === 'single_trust' || selectedHousehold?.head_of_household?.estate_plan_foundation === 'two_trusts' ? 'TRUST' : 'WILL'}
          trustType={selectedHousehold?.head_of_household?.estate_plan_foundation === 'joint_trust' ? 'joint' : selectedHousehold?.head_of_household?.estate_plan_foundation === 'single_trust' || selectedHousehold?.head_of_household?.estate_plan_foundation === 'two_trusts' ? 'individual' : null}
          paymentOption={
            // Single users always get INDIVIDUAL regardless of willType/trustType
            maritalStatus === 'single'
              ? 'INDIVIDUAL'
              : (selectedHousehold?.head_of_household?.estate_plan_foundation === 'single_will' || selectedHousehold?.head_of_household?.estate_plan_foundation === 'two_wills')  
                ? (willType === 'two_wills' ? 'HOUSEHOLD' : 'INDIVIDUAL')
                : (trustType === 'single_trust' ? 'INDIVIDUAL' : 'HOUSEHOLD')
          }
          ud_id={userData?.ud_id || null}
          stateEstateDoc={null} // Not editing an existing doc
          // Stripe props from household context
          paymentRequiredData={paymentRequiredData}
          paymentRequiredLoading={paymentRequiredLoading}
          paymentsSaving={paymentsSaving}
          currentPaymentIntent={currentPaymentIntent}
          handleCreateOrUpdatePaymentIntent={handleCreateOrUpdatePaymentIntent}
          handleClearPaymentIntent={handleClearPaymentIntent}
          handleGetPaymentRequired={handleGetPaymentRequired}
          createSetupIntent={createSetupIntent}
          createTrialSubscription={createTrialSubscription}
          // Document-specific props
          handleEstateDocPartialUpdate={async () => {
            // Not needed for plan selection
            return {} as any;
          }}
          handleChange={() => {
            // Not needed for plan selection
          }}
          setPaywallSuccess={(success) => {
            if (success) {
              // Payment/payment method collection succeeded
              // Continue with selected plan
            }
          }}
          setPaymentRequired={() => {
            // Not needed for plan selection
          }}
        />
      )}
    </Stack>
  );
};
